import type { FC, CSSProperties } from 'react';
import classnames from 'classnames';

import { Indicator as Label } from 'app/components/Text';
import Button from 'app/components/Button/ResetedButton';

import style from './style.scss';

type Color = 'grey' | 'blue' | 'red' | 'green' | 'orange';

type Size = 'small' | 'smallplus' | 'medium' | 'mediumplus' | 'big' | 'bigplus';

interface Props {
  label?: string;
  selected: boolean;
  size?: Size;
  selectedColor?: Color;
  unselectedColor?: Color;
  disabled?: boolean;
  loading?: boolean;
  onlyIndicator?: boolean;
  onChange?: (selected: boolean) => void;
  className?: string;
  labelClassName?: string;
  style?: CSSProperties;
  id?: string;
  indicatorText?: string;
  variation?: 'boolean' | 'mode';
}

const sizeClassMap: Record<Size, string> = {
  small: style.container_small,
  smallplus: style.container_smallplus,
  medium: style.container_medium,
  mediumplus: style.container_mediumplus,
  big: style.container_big,
  bigplus: style.container_bigplus
};

const colorClassMap: { [key: string]: string } = {
  grey: style.container_grey,
  blue: style.container_blue,
  red: style.container_red,
  green: style.container_green,
  orange: style.container_orange
};

const Switch: FC<Props> = ({
  label,
  selected,
  size = 'medium',
  selectedColor = 'green',
  unselectedColor = 'grey',
  disabled,
  loading,
  onlyIndicator,
  onChange,
  className,
  labelClassName: labelStyle,
  style: css,
  id: overrideId,
  indicatorText = null,
  variation = 'boolean'
}) => {
  const onClick =
    onChange && !disabled && !loading && !onlyIndicator ? () => onChange(!selected) : undefined;
  const labelClassName = classnames(
    style.label,
    {
      [style.label_disabled]: disabled
    },
    labelStyle
  );
  const sizeClassName = indicatorText ? sizeClassMap.bigplus : sizeClassMap[size];
  const colorClassName = selected ? colorClassMap[selectedColor] : colorClassMap[unselectedColor];
  const containerClassName = classnames(style.container, sizeClassName, colorClassName, {
    [style.container_disabled]: disabled,
    [style.container_loading]: loading,
    [style.container_mode]: variation === 'mode'
  });
  const indicatorClassName = classnames(style.indicator, {
    [style.indicator_selected]: selected,
    [style.indicator_mode]: variation === 'mode'
  });

  const ContainerElement = onlyIndicator ? 'span' : Button;
  const LabelContainerElement = onlyIndicator ? Label : 'label';

  const id = overrideId ?? (label ? label.trim().toLowerCase().replaceAll(' ', '_') : '');

  return (
    <div className={className} style={css}>
      <ContainerElement
        className={containerClassName}
        disabled={disabled}
        onClick={onClick}
        id={id}
      >
        <div className={style.background_left} />
        <div className={style.background_right} />
        <div className={style.background} />
        <div className={indicatorClassName}>{indicatorText}</div>
        {loading && <div className={style.loader} />}
      </ContainerElement>

      {label && (
        <LabelContainerElement title={label} className={labelClassName} htmlFor={id}>
          {label}
        </LabelContainerElement>
      )}
    </div>
  );
};

Switch.displayName = 'Switch';

export default Switch;
export type { Color, Size };
